import { Coffee, Droplets, UtensilsCrossed, Truck, MapPin, HandHeart, Users, AlertTriangle, Sparkles, Check, Wind } from "lucide-react";
import { IMAGES, SITE } from "@/config/site";
import { Seo } from "@/components/site/Seo";
import { PageHero } from "@/components/site/PageHero";
import { Photo } from "@/components/site/Photo";
import { Reveal, SectionHeading, CtaLink, DonateButton } from "@/components/site/Primitives";
import { FormBuilder } from "@/components/forms/FormBuilder";
import { AGENCY_FIELDS, LIFELINE_INTEREST_FIELDS } from "@/components/forms/formDefs";
import { PROGRAMS, CANTEEN_PHOTOS } from "@/content/content";

const CANTEEN_FEATURES = [
  { icon: Droplets, t: "Water and electrolyte beverages", d: "Drinking water and electrolyte beverages to help crews rehydrate during long operations." },
  { icon: Coffee, t: "Coffee and refreshments", d: "Coffee and other refreshments for responders working through long shifts and overnight incidents." },
  { icon: UtensilsCrossed, t: "Snacks, food, and hot meals", d: "Snacks and food, with hot meals provided when available." },
  { icon: HandHeart, t: "Nonmedical responder support", d: "A place for crews to pause, rest, and recover. Canteen 33 does not provide clinical or medical rehabilitation." },
  { icon: Truck, t: "Logistical assistance", d: "Logistical help for requesting agencies during extended incidents." },
  { icon: MapPin, t: "Where we serve", d: "Primarily Palm Beach County. During major disasters, Canteen 33 may respond elsewhere in Florida when requested and authorized." },
];

const POLICY = [
  "Canteen 33 responds only when requested or authorized by an appropriate public safety agency.",
  "Canteen 33 does not self-deploy.",
  "All deployments must be coordinated with the requesting agency.",
  "Deployment authorization currently rests with Foundation President Dwight Saxon.",
];

export const ProgramCard = ({ p, index }) => (
  <Reveal className="group grid items-center gap-10 border-t border-navy/15 py-14 lg:grid-cols-[1fr_1.1fr] lg:gap-20" data-testid={`program-card-${p.id}`}>
    <div className={index % 2 ? "lg:order-2" : ""}>
      {p.image ? <Photo image={p.image} ratio="aspect-[16/11]" showCaption /> : <LifelineMark />}
    </div>
    <div>
      <p className="flex items-center gap-4 font-display text-sm font-bold text-gold-deep">0{index + 1}{p.status === "coming-soon" && <ComingSoon />}</p>
      <p className="mt-4 text-xs font-semibold uppercase tracking-[0.22em] text-slate">{p.tagline}</p>
      <h3 className="mt-3 text-3xl font-extrabold md:text-5xl">{p.name}</h3>
      <p className="mt-6 max-w-xl text-base leading-relaxed text-ink/80 md:text-lg">{p.summary}</p>
      <div className="mt-8"><CtaLink to={p.to} testId={`program-card-${p.id}-button`}>{p.cta}</CtaLink></div>
    </div>
  </Reveal>
);

const ComingSoon = ({ light = false }) => (
  <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 font-display text-[11px] font-bold uppercase tracking-[0.18em] ${light ? "border-gold/60 text-gold" : "border-gold text-gold-deep bg-gold/10"}`} data-testid="coming-soon-badge">
    <span className="h-1.5 w-1.5 rounded-full bg-gold" aria-hidden="true" /> Coming Soon
  </span>
);

const LifelineMark = () => (
  <div className="grain relative flex aspect-[16/11] items-center justify-center overflow-hidden bg-navy" data-testid="lifeline-icon-panel">
    <span className="absolute h-56 w-56 rounded-full border border-gold/25 md:h-72 md:w-72" aria-hidden="true" />
    <span className="absolute h-36 w-36 rounded-full border border-gold/40 md:h-48 md:w-48" aria-hidden="true" />
    <HandHeart className="relative text-gold" size={84} strokeWidth={1.25} aria-hidden="true" />
  </div>
);

const LIFELINE_NEEDS = ["Essential household expenses", "Utilities", "Housing", "Medical-related needs", "Emergency transportation", "Funeral expenses"];
const LIFELINE_SERVES = ["Firefighters", "Law enforcement officers", "EMTs and paramedics", "Public safety dispatchers"];

const Lifeline = () => (
  <section id="lifeline-assistance" className="border-t border-navy/10 bg-white py-24 md:py-32" data-testid="programs-lifeline-section">
    <div className="container-x grid gap-14 lg:grid-cols-[1.1fr_0.9fr] lg:gap-20">
      <div>
        <div className="flex flex-wrap items-center gap-4"><p className="eyebrow">Lifeline Assistance Program</p><ComingSoon /></div>
        <h2 className="mt-5 text-3xl font-extrabold leading-[1.08] tracking-tight sm:text-4xl lg:text-5xl">When Those Who Serve Need Someone in Their Corner.</h2>
        <p className="mt-4 font-display text-sm font-semibold uppercase tracking-[0.16em] text-slate">First Responders Lifeline Assistance Program · Emergency Financial Assistance &amp; Family Support</p>
        <Reveal className="mt-8 space-y-5 text-base leading-relaxed text-ink/85 md:text-lg">
          <p>When serious illness, unexpected injury, or the loss of a first responder creates financial hardship, families may find themselves facing overwhelming challenges.</p>
          <p>First Responders Foundation is developing the Lifeline Assistance Program to provide compassionate, short-term emergency financial assistance to eligible first responders and surviving family members.</p>
          <p>Whether the hardship results from an on-duty or off-duty incident, our goal is to help families meet essential needs during difficult times.</p>
        </Reveal>
        <Reveal className="mt-10 flex items-start gap-4 border-l-2 border-gold bg-paper p-6" data-testid="lifeline-status-notice">
          <AlertTriangle className="mt-0.5 shrink-0 text-gold-deep" size={22} />
          <div className="text-sm leading-relaxed text-ink">
            <p className="font-display font-bold uppercase tracking-[0.14em] text-navy">Coming Soon — Program Under Development</p>
            <p className="mt-2">The Lifeline Assistance Program is not yet accepting applications or providing financial assistance. Additional information will be announced once program policies, funding, and approval procedures are finalized.</p>
          </div>
        </Reveal>
        <Reveal className="mt-10 border border-navy/10 bg-paper p-6 md:p-8" data-testid="lifeline-interest-block">
          <h3 className="text-xl font-bold">Get Notified When the Program Opens</h3>
          <p className="mt-2 mb-8 text-sm leading-relaxed text-ink/75">First responders, families, and agencies can join our interest list. We will email you when the program begins accepting applications. Joining the list is not an application and does not reserve or guarantee assistance.</p>
          <FormBuilder
            formType="lifeline-interest"
            fields={LIFELINE_INTEREST_FIELDS}
            testId="lifeline-interest-form"
            submitLabel="Notify Me"
            successTitle="You're on the list."
            successText="Thank you. We will email you when the Lifeline Assistance Program opens. If you are facing an emergency today, please contact your agency's employee assistance resources, or call or text 988 for the Suicide & Crisis Lifeline."
          />
        </Reveal>
      </div>
      <div className="space-y-5">
        <Reveal className="bg-navy p-8 text-paper md:p-10" data-testid="lifeline-planned-details">
          <HandHeart className="text-gold" size={30} />
          <h3 className="mt-6 text-xl font-bold text-paper">Who the program will initially serve</h3>
          <p className="mt-3 text-sm leading-relaxed text-[#C9D4DF]">Active and retired first responders, and their families, who reside in or have a qualifying current or former employment connection to Palm Beach or Broward County, Florida:</p>
          <ul className="mt-4 grid gap-2 sm:grid-cols-2">
            {LIFELINE_SERVES.map((s) => <li key={s} className="flex items-start gap-2 text-sm text-[#D5DEE7]"><Check className="mt-0.5 shrink-0 text-gold" size={16} />{s}</li>)}
          </ul>
        </Reveal>
        <Reveal delay={0.08} className="border border-navy/10 bg-paper p-8 md:p-10">
          <h3 className="text-xl font-bold">Potential assistance may include</h3>
          <ul className="mt-4 grid gap-2 sm:grid-cols-2">
            {LIFELINE_NEEDS.map((s) => <li key={s} className="flex items-start gap-2 text-sm text-ink/85"><Check className="mt-0.5 shrink-0 text-gold-deep" size={16} />{s}</li>)}
          </ul>
          <div className="mt-8 border-t border-navy/10 pt-6">
            <p className="text-xs leading-relaxed text-slate">Any assistance will be subject to documented financial need, available funding, and Foundation approval. Assistance is not guaranteed.</p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const Canteen = () => (
  <section id="canteen-33" className="grain relative overflow-hidden bg-navy py-24 text-paper md:py-32" data-testid="programs-canteen-section">
    <span className="outline-num pointer-events-none absolute -left-4 bottom-0 select-none text-[12rem] md:text-[22rem]" aria-hidden="true">33</span>
    <div className="container-x relative">
      <div className="grid gap-14 lg:grid-cols-[1.1fr_0.9fr] lg:gap-20">
        <SectionHeading light eyebrow="Canteen 33" title="Rest, Rehydrate, Return to Work." intro="Canteen 33 is the Foundation's mobile canteen and nonmedical rehabilitation support program. Serving primarily Palm Beach County, it supports firefighters, law enforcement officers, and other responders during extended incidents, training exercises, and authorized deployments, and may respond elsewhere in Florida during major disasters when requested and authorized." />
        <div>
          <Photo image={CANTEEN_PHOTOS[0]} ratio="aspect-[4/3]" dark showCaption testId="canteen-photo-1" />
          <div className="mt-5 grid grid-cols-2 gap-5">
            {CANTEEN_PHOTOS.slice(1).map((p, i) => <Photo key={p.src} image={p} ratio="aspect-square" dark showCaption testId={`canteen-photo-${i + 2}`} />)}
          </div>
        </div>
      </div>
      <div className="mt-16 grid gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
        {CANTEEN_FEATURES.map((f, i) => (
          <Reveal key={f.t} delay={i * 0.06} className="bg-navy p-8" data-testid={`canteen-feature-${i + 1}`}>
            <f.icon className="text-gold" size={26} />
            <h3 className="mt-8 text-lg font-bold text-paper">{f.t}</h3>
            <p className="mt-3 text-sm leading-relaxed text-[#C9D4DF]">{f.d}</p>
          </Reveal>
        ))}
      </div>
      <div className="mt-10 grid gap-5 lg:grid-cols-2">
        <Reveal className="border-l-2 border-fire bg-white/5 p-6 md:p-8" data-testid="canteen-deployment-policy">
          <p className="flex items-center gap-3 font-display text-sm font-bold uppercase tracking-[0.18em] text-paper"><AlertTriangle className="text-[#E57C7C]" size={20} /> Deployment Policy</p>
          <ul className="mt-5 space-y-3">
            {POLICY.map((p) => <li key={p} className="flex items-start gap-3 text-sm leading-relaxed text-[#D5DEE7]"><Check className="mt-0.5 shrink-0 text-gold" size={16} />{p}</li>)}
          </ul>
          <p className="mt-5 text-xs leading-relaxed text-[#AFBFCE]">Emergency deployment requests must follow the Foundation's established agency-contact procedures and should not be submitted through this website.</p>
        </Reveal>
        <Reveal delay={0.08} className="border-l-2 border-gold bg-white/5 p-6 md:p-8" data-testid="canteen-volunteer-requirements">
          <p className="flex items-center gap-3 font-display text-sm font-bold uppercase tracking-[0.18em] text-paper"><Users className="text-gold" size={20} /> Volunteer Requirements</p>
          <p className="mt-5 text-sm leading-relaxed text-[#D5DEE7]">Approved volunteers must complete Foundation orientation and required NIMS/ICS training, as established by Foundation policy, including:</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {["ICS-100", "ICS-200", "ICS-300", "IS-700"].map((c) => <span key={c} className="border border-gold/50 px-3 py-1 font-display text-xs font-bold text-gold">{c}</span>)}
          </div>
          <p className="mt-6 flex items-start gap-3 text-sm leading-relaxed text-[#D5DEE7]"><Wind className="mt-0.5 shrink-0 text-gold" size={16} /><span><strong className="text-paper">Looking ahead:</strong> as funding permits, future equipment may include cooling fans, chairs, shade tents, and other rehabilitation resources.</span></p>
        </Reveal>
      </div>
      <div className="mt-10 flex flex-col gap-3 sm:flex-row">
        <DonateButton label="Support Canteen 33" href={SITE.donateLinks.canteen} variant="btn-gold" testId="canteen-donate-button" />
        <CtaLink to="/get-involved#volunteer" variant="btn-ghost-light" testId="canteen-volunteer-button">Volunteer</CtaLink>
      </div>
    </div>
  </section>
);

const AgencyForm = () => (
  <section id="agency-request" className="py-24 md:py-32" data-testid="programs-agency-section">
    <div className="container-x grid gap-14 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20">
      <div>
        <SectionHeading eyebrow="For Agencies" title="Agency Support Inquiry." intro="Fire departments, law enforcement agencies, and other public safety organizations can use this form to plan ahead, request information, or discuss support for training events and scheduled operations." />
        <Reveal delay={0.1} className="mt-8 border-l-2 border-fire bg-white p-6 text-sm leading-relaxed text-ink/85">
          Completing this form does not guarantee deployment or availability. For active emergencies, agencies must use the Foundation's established agency-contact procedures.
        </Reveal>
      </div>
      <Reveal delay={0.1} className="border border-navy/10 bg-white p-6 shadow-[0_12px_40px_rgba(16,38,63,0.08)] md:p-10">
        <FormBuilder
          formType="agency"
          fields={AGENCY_FIELDS}
          testId="agency-form"
          submitLabel="Submit Agency Inquiry"
          successTitle="Thank you. Your agency inquiry has been received."
          successText="A Foundation representative will review your request and follow up using the contact information provided. Please remember that this inquiry does not confirm deployment or availability."
          footnote="This form is for planning and information requests only, not for active emergencies."
        />
      </Reveal>
    </div>
  </section>
);

const Future = ({ items }) => (
  <section className="border-t border-navy/10 bg-mist py-24 md:py-32" data-testid="programs-future-section">
    <div className="container-x">
      <SectionHeading eyebrow="Looking Ahead" title="Future Programs." intro="As the Foundation grows, new initiatives will be introduced only once they are fully operational and responsibly resourced." />
      {items.length ? (
        <div className="mt-6">{items.map((p, i) => <ProgramCard key={p.id} p={p} index={i + 2} />)}</div>
      ) : (
        <Reveal className="mt-12 flex items-start gap-5 border border-dashed border-navy/25 bg-paper p-8 md:p-10">
          <Sparkles className="shrink-0 text-gold-deep" size={26} />
          <p className="max-w-2xl leading-relaxed text-ink/80">New programs will be announced here when they launch. Have an idea for how the Foundation can support those who serve? <a href="/about#contact" className="font-semibold text-navy underline underline-offset-4" data-testid="future-programs-contact-link">Share it with us.</a></p>
        </Reveal>
      )}
    </div>
  </section>
);

export default function Programs() {
  const active = PROGRAMS.filter((p) => p.status !== "future");
  const future = PROGRAMS.filter((p) => p.status === "future");
  return (
    <>
      <Seo title="Our Programs | Service Dogs & Canteen 33 Responder Support" description="First Responders Foundation programs: a free Service Dog Program with Big Dog Ranch Rescue, and Canteen 33 mobile canteen and nonmedical rehab support in Palm Beach County." path="/programs" />
      <PageHero eyebrow="Our Programs" lines={[{ text: "Programs Built" }, { text: "Around Those", className: "text-gold" }, { text: "Who Serve." }]} intro="Each Foundation initiative is designed to meet a real need for first responders and veterans, delivered with care, coordination, and accountability." image={IMAGES.fireScene} />
      <section className="py-20 md:py-28" data-testid="programs-list-section">
        <div className="container-x">
          {active.map((p, i) => <ProgramCard key={p.id} p={p} index={i} />)}
        </div>
      </section>
      <Canteen />
      <AgencyForm />
      <Lifeline />
      <Future items={future} />
    </>
  );
}
